import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { api } from "../../lib/api";
import { AlertTriangle } from "lucide-react";
import { Kpi, Th, Td, Pill, PlatformBadge, SectionTitle, Loading, usd, num } from "./shared";

const AUTONOMY = {
  L0: { label: "L0 · Manual", cls: "bg-[hsl(var(--cs-tint))] text-muted-foreground border-border" },
  L1: { label: "L1 · Advise", cls: "bg-[#e0f2fe] text-[#0369a1] border-[#bae6fd]" },
  L2: { label: "L2 · Approve", cls: "bg-[#ede9fe] text-[#6d28d9] border-[#ddd6fe]" },
  L3: { label: "L3 · Autonomous", cls: "bg-[#dcfce7] text-[#15803d] border-[#bbf7d0]" },
};

export const KubeFleet = ({ onOpenCluster }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.kube.fleet().then(setData).catch(console.error);
  }, []);

  if (!data) return <div className="px-6 lg:px-10 py-8"><Loading label="Loading fleet…" testId="kube-fleet-loading" /></div>;
  const k = data.kpis;

  return (
    <div className="px-6 lg:px-10 pt-6 pb-8 space-y-6" data-testid="kube-fleet">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi testId="kube-kpi-coverage" label="Managed node coverage" value={`${k.coverage_pct}%`}
          sub={`${num(k.nodes_managed)} of ${num(k.nodes_total)} nodes`} />
        <Kpi testId="kube-kpi-spend" label="Container spend / mo" value={usd(k.monthly_spend_usd)}
          sub={`${num(k.clusters)} clusters · ${num(k.pods)} pods`} />
        <Kpi testId="kube-kpi-savings" label="Savings delivered" value={usd(k.savings_usd)}
          sub={`${k.savings_pct}% of pre-optimization cost`} accent="text-[#15803d]" />
        <Kpi testId="kube-kpi-missed" label="Missed savings" value={usd(k.missed_savings_usd)}
          sub={`${k.unmanaged_nodes} unmanaged nodes`} accent="text-[#be185d]" />
        <Kpi testId="kube-kpi-autonomy" label="Autonomy mix"
          value={`${k.autonomy_mix.L3} / ${k.clusters}`} sub="clusters at L3 autonomous" />
        <Kpi testId="kube-kpi-pods" label="Workload pods" value={num(k.pods)} sub="across all clusters" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cs-card p-5 lg:col-span-2" data-testid="kube-daily-cost">
          <SectionTitle hint="Partial days are marked — the current day is still accruing.">Container cost · last 14 days</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.daily_cost} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={1} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => usd(v, false)} />
              <Bar dataKey="cost_usd" radius={[2, 2, 0, 0]}>
                {data.daily_cost.map((d, i) => (
                  <Cell key={i} fill={d.partial ? "#cbd5e1" : "#00579e"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="cs-card p-5" data-testid="kube-levers">
          <SectionTitle hint="Each lever is measured on its own so it can be verified independently.">Savings by lever · 30 days</SectionTitle>
          <div className="space-y-2.5">
            {data.levers.map((l) => (
              <div key={l.id} data-testid={`kube-lever-${l.id}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">{l.label}</span>
                  <span className="cs-mono font-semibold" style={{ color: l.color }}>{usd(l.savings_usd)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[hsl(var(--cs-tint))] mt-1 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${(l.savings_usd / data.levers[0].savings_usd) * 100}%`, background: l.color }} />
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{l.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cs-card p-5" data-testid="kube-cluster-table">
        <SectionTitle hint="Health checks: an out-of-date agent blocks L3 autonomy; unschedulable pods block consolidation.">Clusters</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <Th>Cluster</Th><Th>Platform</Th><Th>K8s</Th><Th>In-place resize</Th><Th>Autonomy</Th>
                <Th right>Nodes</Th><Th right>Pods</Th><Th right>Spend / mo</Th><Th right>Network</Th><Th>Health</Th>
              </tr>
            </thead>
            <tbody>
              {data.clusters.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-[hsl(var(--cs-tint))] cursor-pointer transition-colors"
                  onClick={() => onOpenCluster?.(c.id)} data-testid={`kube-cluster-row-${c.name}`}>
                  <Td className="font-medium">{c.name}</Td>
                  <Td><PlatformBadge platform={c.platform} provider={c.provider} /></Td>
                  <Td mono>{c.k8s_version}</Td>
                  <Td mono className={c.in_place_resize ? "text-[#15803d]" : "text-muted-foreground"}>
                    {c.in_place_resize ? "supported" : "restart needed"}
                  </Td>
                  <Td><Pill cls={AUTONOMY[c.autonomy].cls}>{AUTONOMY[c.autonomy].label}</Pill></Td>
                  <Td right mono>{c.nodes_managed}/{c.nodes_total} <span className="text-muted-foreground">({c.coverage_pct}%)</span></Td>
                  <Td right mono>{num(c.pods)}</Td>
                  <Td right mono className="font-semibold">{usd(c.monthly_spend_usd)}</Td>
                  <Td right mono>{usd(c.network_usd)}</Td>
                  <Td>
                    {c.health.length ? (
                      <span className="inline-flex items-center gap-1 text-[#92400e]" title={c.health.map((h) => h.message).join(" · ")}>
                        <AlertTriangle className="w-3 h-3" /> {c.health.length}
                      </span>
                    ) : <span className="text-[#15803d]">healthy</span>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KubeFleet;
